import { storage } from '../lib/firebase';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { compressImageFile } from '../utils/imageCompress';

type UploadImageOpts = {
  maxSizeMB?: number;
  maxDim?: number;
  contentType?: 'image/jpeg' | 'image/webp';

  /**
   * Use when the caller has already normalized/compressed the image.
   * Prevents mobile devices from decoding and recompressing a large
   * camera photo multiple times.
   */
  alreadyCompressed?: boolean;
};

export async function uploadImage(
  file: File,
  path: string,
  opts?: UploadImageOpts,
): Promise<string> {
  const contentType: 'image/jpeg' | 'image/webp' =
    opts?.contentType ?? 'image/jpeg';

  const uploadBody: Blob = opts?.alreadyCompressed
    ? file
    : await compressImageFile(file, {
        maxDim: opts?.maxDim ?? 1600,
        mimeType: contentType,
        quality: 0.82,
      });

  const maxBytes = Math.max(1, opts?.maxSizeMB ?? 10) * 1024 * 1024;

  if (uploadBody.size > maxBytes) {
    throw new Error(
      `IMAGE_UPLOAD_TOO_LARGE:${uploadBody.size}:${maxBytes}`,
    );
  }

  const storageRef = ref(storage, path);

  await uploadBytes(storageRef, uploadBody, {
    contentType,
    cacheControl: 'public,max-age=31536000',
  });

  return getDownloadURL(storageRef);
}

export async function uploadScanImage(
  file: File,
  uid: string,
  scanId: string,
  alreadyCompressed = false,
): Promise<string> {
  const safeUid = uid || 'unknown';
  const safeScanId = scanId || `${Date.now()}`;

  return uploadImage(
    file,
    `fishIQScans/${safeUid}/${safeScanId}.jpg`,
    {
      contentType: 'image/jpeg',
      maxDim: 1400,
      maxSizeMB: 10,
      alreadyCompressed,
    },
  );
}

function storagePathFromDownloadUrl(value: string): string | null {
  const raw = String(value || '').trim();
  if (!raw) return null;

  try {
    const parsed = new URL(raw);

    if (parsed.hostname === 'firebasestorage.googleapis.com') {
      const marker = '/o/';
      const index = parsed.pathname.indexOf(marker);
      if (index >= 0) return decodeURIComponent(parsed.pathname.slice(index + marker.length));
    }

    if (parsed.hostname === 'storage.googleapis.com') {
      const parts = parsed.pathname.split('/').filter(Boolean);
      if (parts.length >= 2) return decodeURIComponent(parts.slice(1).join('/'));
    }
  } catch {}

  return null;
}

export async function deleteCatchStorageImages(input: {
  uid: string;
  catchId: string;
  urls?: Array<string | null | undefined>;
  includeCommunity?: boolean;
}): Promise<{ deleted: number; skipped: number; failed: number }> {
  const uid = String(input.uid || '').trim();
  const catchId = String(input.catchId || '').trim();
  if (!uid || !catchId) return { deleted: 0, skipped: 0, failed: 0 };

  const allowedPrefixes = [
    `catchDiaryPrivate/${uid}/${catchId}/`,
    `catchDiaryShared/${uid}/${catchId}/`,
    `catchDiary/${uid}/${catchId}/`,
    ...(input.includeCommunity === true ? [`community/${uid}/${catchId}/`] : []),
  ];

  const paths = Array.from(
    new Set(
      (input.urls || [])
        .map((url) => storagePathFromDownloadUrl(String(url || '')))
        .filter((value): value is string => Boolean(value)),
    ),
  );

  let deleted = 0;
  let skipped = 0;
  let failed = 0;

  for (const path of paths) {
    // Never allow this client cleanup helper anywhere near immutable tournament evidence.
    if (path.startsWith('tournamentEvidence/')) {
      skipped += 1;
      continue;
    }

    if (!allowedPrefixes.some((prefix) => path.startsWith(prefix))) {
      skipped += 1;
      continue;
    }

    try {
      await deleteObject(ref(storage, path));
      deleted += 1;
    } catch (error: any) {
      if (error?.code === 'storage/object-not-found') {
        deleted += 1;
        continue;
      }
      failed += 1;
      console.warn('[imageUpload] catch image cleanup failed', { path, error });
    }
  }

  return { deleted, skipped, failed };
}
